import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar, Footer, LoadingPage } from './components/UI';
import {
  LandingPageSEO, LoginPageSEO, TermsPageSEO, PrivacyPageSEO,
  ContactPageSEO, DonatePageSEO, AuthenticatedPageSEO
} from './components/SEO';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import DonatePage from './pages/DonatePage';
import PublicLeaderboardPage from './pages/PublicLeaderboardPage';
import HomePage from './pages/HomePage';
import CourseLeaderboardPage from './pages/CourseLeaderboardPage';
import AddCoursePage from './pages/AddCoursePage';
import ProfilePage from './pages/ProfilePage';
import PeopleSearchPage from './pages/PeopleSearchPage';
import LocalLegendsPage from './pages/LocalLegendsPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Layout wrapper used for all authenticated pages (includes Navbar + Footer)
function AppLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

// Layout for public pages that still want a Navbar/Footer (but no auth required)
function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1">{children}</main>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;

  return (
    <Routes>
      {/* ── Public / marketing routes ─────────────────────────────────── */}
      <Route path="/" element={user ? <Navigate to="/home" replace /> : <><LandingPageSEO /><LandingPage /></>} />
      <Route path="/login" element={user ? <Navigate to="/home" replace /> : <><LoginPageSEO /><LoginPage /></>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/terms" element={<><TermsPageSEO /><TermsPage /></>} />
      <Route path="/privacy" element={<><PrivacyPageSEO /><PrivacyPolicyPage /></>} />
      <Route path="/contact" element={<><ContactPageSEO /><ContactPage /></>} />
      <Route path="/donate" element={<><DonatePageSEO /><DonatePage /></>} />

      {/* Public leaderboard — no login required, read-only */}
      <Route path="/public/leaderboard/:teeId" element={
        <PublicLayout><PublicLeaderboardPage /></PublicLayout>
      } />

      {/* ── Authenticated routes (noindex) ────────────────────────────── */}
      <Route path="/home" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Leaderboards" /><HomePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/leaderboard/course/:courseId" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Course Leaderboard" /><CourseLeaderboardPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/add-course" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Add a Course" /><AddCoursePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Player Profile" /><ProfilePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/people" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Find Golfers" /><PeopleSearchPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/local-legends" element={
        <ProtectedRoute>
          <AppLayout><AuthenticatedPageSEO title="Local Legends" /><LocalLegendsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}